/**
 * Frontend-only friendly-word lookup for EVA agent activity (spec 042 FR-008/FR-011). The backend
 * records only raw step keys / reason codes / outcome names — all plain-English wording lives here so
 * it can change without a backend deploy. Every lookup gracefully falls back to the raw value when
 * unmapped, so a newly-added step/reason never renders blank.
 */

const STEP_LABELS: Record<string, string> = {
  'pick-up-case': 'Picking up the case',
  'validate-patient': 'Validating patient demographics',
  'edi-inquiry': 'Checking eligibility with the payer',
  'portal-fallback': 'Checking the payer portal',
  'parse-benefits': 'Parsing benefit details',
  'apply-payer-rules': 'Applying payer rules',
  'extract-procedures': 'Extracting procedures from the note',
  'calc-reimbursement': 'Calculating reimbursement',
  'calculate-epr': 'Calculating estimated patient responsibility',
  writeback: 'Saving results to the verification',
};

/**
 * Fuller, plain-English sentence for each raw reason code — used on the roomy detail banner and the
 * Agent-tab outcome summary. Keyed by the exact code the backend records (see the Verifications
 * reason catalogs: pre-inquiry, inquiry-prep, EDI, benefits, extraction, reimbursement).
 *
 * ONE exception, and it is deliberate: the four `EVA_TRIGGER_*` codes (spec 050) ride
 * `EvaDetails.runHistory[].outcome`, NOT `lastRunReasonCode`. They live here anyway because
 * {@link OUTCOME_LABELS} is a closed four-member set that the severity/colour switches in
 * `EvaOutcomeBanner` and `EvaAgentTimeline` exhaustively assume. When a run-history renderer is
 * built, call {@link friendlyReasonLabel} for those entries — reaching for
 * {@link friendlyOutcomeLabel} by field-name reflex silently falls back to the raw code.
 */
const REASON_LABELS: Record<string, string> = {
  // Extraction / coding
  CodingReviewRequired: 'This case needs a coding review before it can proceed.',
  ExtractionParseError: 'EVA could not parse the procedures from the note.',
  NoAppointmentNote: 'There is no appointment note to extract procedures from.',
  NoProceduresExtracted: 'No procedures were extracted from the appointment note.',
  // Pre-inquiry: demographics / facility / payer
  MissingPatientDemographics: 'Patient demographic information is missing or invalid.',
  InvalidDobFormat: 'The date of birth is malformed or is a future date.',
  MissingMemberId: 'The primary insurance policy number (member ID) is missing.',
  SsnMismatch: 'The case SSN does not match the source record SSN (Medicare check).',
  FacilityNotConfigured: 'The facility is not present in the configuration service.',
  MissingFacilityNpiTin: 'The facility is missing a required provider NPI or billing TIN.',
  MissingFacilityOrganizationName:
    'The facility organization name is blank; it is required to build the eligibility inquiry.',
  FacilityRenderingBillingMismatch: 'The rendering provider NPI does not match the billing facility NPI.',
  PayerNotSupported: 'The resolved payer is not supported for EVA or has no trading-partner mapping.',
  TraditionalMedicareNotAcceptedAtFacility:
    'This facility does not accept traditional Medicare; manual processing is required.',
  UhcTxonexPlanDetected: 'A UHC Texas One Exchange (TXONEX) plan was detected; it cannot be auto-processed by EVA.',
  SurestPlanDetected:
    'A Surest plan was detected; Surest prices each procedure separately, so benefits must be confirmed with the payer before quoting.',
  ValidationError: 'The case failed a required pre-inquiry validation.',
  FacilityServiceTypeMismatch:
    'The inpatient/outpatient designation is inconsistent with the configured facility type.',
  AdmitTypeUndeterminable: 'The case has no inpatient/outpatient designation and one could not be derived.',
  UnsupportedFacilityType: 'The facility type is not mappable to an EVA service-type strategy.',
  // EDI inquiry
  TradingPartnerNotConfigured: 'No trading-partner mapping exists for this payer, so the inquiry cannot be routed.',
  ClearinghouseTransientFailure: 'The clearinghouse returned a transient error; the inquiry may succeed on retry.',
  ClearinghouseNonRetryableFailure: 'The clearinghouse returned a permanent error; retrying will not resolve it.',
  PayerRejection: 'The payer rejected the eligibility inquiry (AAA rejection segments).',
  MemberNotFound: 'The payer could not identify the subscriber; the member ID or demographics may be incorrect.',
  BlueCardUnresolved: 'This is a BlueCard member and the home Blue plan did not respond to the real-time inquiry.',
  NoEBSegmentsFound: 'The eligibility response contained no benefit segments for the selected service type.',
  BlueDistinctionFacility: 'The response carries a Blue Distinction designation that requires review.',
  // Benefits
  NoEligibilityResponse: 'The payer returned no eligibility data; the response is missing or unparseable.',
  NoBenefitDataExtracted: 'The response contained no benefit segments applicable to the selected service type.',
  NoInNetworkBenefits: 'The response contains no in-network benefits for the selected service type.',
  BcbsFederalMissingCopay: 'BCBS Federal benefits require a copay row, but none was found in the response.',
  MedicarePartARequired: 'Medicare inpatient benefits require Part A rows, which were not found.',
  MedicarePartBRequired: 'Medicare outpatient benefits require Part B rows, which were not found.',
  BenefitsTerminatedCoverage: "The payer response indicates the member's coverage is no longer active.",
  TerminatedCoverage: "The payer response indicates the member's coverage is no longer active.",
  DualCoverageDetected: 'The response indicates the member may have coverage under more than one plan.',
  MedicareAdvantageDetected:
    'The Medicare response names another payer or carries a Medicare Advantage message, so the member is likely enrolled in an MA plan rather than traditional Medicare.',
  BlueDistinctionFacilityDetected:
    'The response carries a Blue Distinction designation, which always requires human review.',
  HcaEmployerPlanDetected:
    "The plan's employer group is Hospital Corporation of America, so this verification cannot be completed electronically.",
  BaylorScottWhitePlanDetected:
    "The plan's employer group is Baylor Scott & White, so this verification cannot be completed electronically.",
  ChristusPlanDetected:
    "The plan's employer group is Christus Health, so this verification cannot be completed electronically.",
  UhcCobStatusUnknown:
    'The secondary-insurance status and the response COB data disagree; UHC COB cannot be confirmed automatically.',
  UhcSnpReview:
    'The UHC plan is a Special Needs Plan (SNP); C-SNP cost-share may be covered in full and must be confirmed before quoting.',
  BcbsTxAccumulatorReview:
    "BCBS Texas 'CH' plans do not return accumulator data (deductible/OOP met) via Availity, so the parsed met amounts cannot be trusted.",
  HmoPlanException: "The member's HMO plan is configured to always route to staff for review.",
  HmoBenefitSourcePending: "The member's HMO plan matched a rule whose benefit source is not yet configured.",
  HmoNoOonBenefits: 'The HMO plan expects out-of-network benefits, but none were returned for the selected service.',
  HmoNoInnBenefits: 'The HMO plan requires in-network benefits, but none were returned for the selected service.',
  // Secondary coverage
  SecondaryUnverified: 'Secondary insurance could not be verified electronically.',
  // Reimbursement
  EVA_REIMB_NO_PROCEDURES: 'There are no procedures to cost for reimbursement.',
  EVA_REIMB_CALC_FAILED: 'The reimbursement calculation could not be completed.',
  EVA_REIMB_ZERO:
    'EVA calculated a total reimbursement of $0, which usually means a missing or zero-rate contract; a human should review this case.',
  // Re-verification
  EVA_PR_CHANGED:
    "Re-verification found the patient's responsibility changed by more than the review threshold; a human should re-call the patient about the new amount.",
  EVA_BENEFITS_CHANGED: 'Benefits changed since the last verification; a review is required.',
  // Lease / run lifecycle
  EVA_RUN_FAILED: 'The run failed without recording a reason; see the run for the step that stopped it.',
  EVA_REVIEW_REQUIRED: 'The run stopped for a manual review without recording a reason.',
  EVA_PAYER_DISABLED: 'The payer left the EVA-enabled set while a run was in progress; the run is presumed lost.',
  EVA_LEASE_EXPIRED:
    "This case's EVA run went stale (its lease expired) and was automatically marked Failed so it can be retried.",
  // EVA Legacy pushback
  EVA_LEGACY_PUSHBACK_FAILED:
    'EVA verified this case, but pushing the result back to the legacy app failed; it will be retried.',
  // Event-triggered re-verification (spec 050 FR-014)
  EVA_TRIGGER_INSURANCE:
    'The primary insurance changed after the last verification, so the earlier benefits no longer apply and EVA will verify this case again.',
  EVA_TRIGGER_DEMOGRAPHICS:
    'The patient name or date of birth changed after the last verification, so the earlier eligibility no longer applies and EVA will verify this case again with the corrected identity.',
  EVA_TRIGGER_DOS:
    'The service date moved, so EVA rescheduled the re-verification against the new date; the existing verification is kept.',
  EVA_TRIGGER_CPT:
    'The scheduled procedure codes changed, so EVA will verify the benefits again for the new procedures; the existing verification is kept.',
};

/**
 * Short (2–3 word) label for the compact worklist-grid chip — the roomy sentence in
 * {@link REASON_LABELS} does not fit a chip. Keyed by the same raw reason codes.
 */
const REASON_CHIP_LABELS: Record<string, string> = {
  // Extraction / coding
  CodingReviewRequired: 'Coding Review',
  ExtractionParseError: 'Extraction Error',
  NoAppointmentNote: 'No Note',
  NoProceduresExtracted: 'No Procedures',
  // Pre-inquiry: demographics / facility / payer
  MissingPatientDemographics: 'Missing Demographics',
  InvalidDobFormat: 'Invalid DOB',
  MissingMemberId: 'Missing Member ID',
  SsnMismatch: 'SSN Mismatch',
  FacilityNotConfigured: 'Facility Not Set Up',
  MissingFacilityNpiTin: 'Missing NPI/TIN',
  MissingFacilityOrganizationName: 'Missing Facility Name',
  FacilityRenderingBillingMismatch: 'NPI Mismatch',
  PayerNotSupported: 'Payer Not Supported',
  TraditionalMedicareNotAcceptedAtFacility: 'Medicare Not Accepted',
  UhcTxonexPlanDetected: 'UHC TXONEX Plan',
  SurestPlanDetected: 'Surest Plan',
  ValidationError: 'Validation Error',
  FacilityServiceTypeMismatch: 'Service Type Mismatch',
  AdmitTypeUndeterminable: 'Admit Type Unknown',
  UnsupportedFacilityType: 'Unsupported Facility',
  // EDI inquiry
  TradingPartnerNotConfigured: 'Payer Not Configured',
  ClearinghouseTransientFailure: 'Clearinghouse Error',
  ClearinghouseNonRetryableFailure: 'Clearinghouse Error',
  PayerRejection: 'Payer Rejection',
  MemberNotFound: 'Member Not Found',
  BlueCardUnresolved: 'BlueCard Unresolved',
  NoEBSegmentsFound: 'No Benefits Returned',
  BlueDistinctionFacility: 'Blue Distinction',
  // Benefits
  NoEligibilityResponse: 'No Eligibility Data',
  NoBenefitDataExtracted: 'No Benefit Data',
  NoInNetworkBenefits: 'No In-Network Benefits',
  BcbsFederalMissingCopay: 'Missing Copay',
  MedicarePartARequired: 'Part A Required',
  MedicarePartBRequired: 'Part B Required',
  BenefitsTerminatedCoverage: 'Terminated Coverage',
  TerminatedCoverage: 'Terminated Coverage',
  DualCoverageDetected: 'Dual Coverage',
  MedicareAdvantageDetected: 'Medicare Advantage',
  BlueDistinctionFacilityDetected: 'Blue Distinction',
  HcaEmployerPlanDetected: 'HCA Employer Plan',
  BaylorScottWhitePlanDetected: 'Baylor Scott & White Plan',
  ChristusPlanDetected: 'Christus Plan',
  UhcCobStatusUnknown: 'COB Unknown',
  UhcSnpReview: 'UHC SNP Review',
  BcbsTxAccumulatorReview: 'BCBS TX Accumulators',
  HmoPlanException: 'HMO Review',
  HmoBenefitSourcePending: 'HMO Pending',
  HmoNoOonBenefits: 'HMO No OON Benefits',
  HmoNoInnBenefits: 'HMO No INN Benefits',
  // Secondary coverage
  SecondaryUnverified: 'Secondary Unverified',
  // Reimbursement
  EVA_REIMB_NO_PROCEDURES: 'No Procedures',
  EVA_REIMB_CALC_FAILED: 'Calc Failed',
  EVA_REIMB_ZERO: 'Zero Reimbursement',
  // Re-verification
  EVA_PR_CHANGED: 'Patient Resp. Changed',
  EVA_BENEFITS_CHANGED: 'Benefits changed',
  // Lease / run lifecycle
  EVA_RUN_FAILED: 'Run Failed',
  EVA_REVIEW_REQUIRED: 'Review Required',
  EVA_PAYER_DISABLED: 'Payer Disabled',
  EVA_LEASE_EXPIRED: 'Lease Expired',
  // EVA Legacy pushback
  EVA_LEGACY_PUSHBACK_FAILED: 'Pushback Failed',
};

const OUTCOME_LABELS: Record<string, string> = {
  Succeeded: 'EVA verified this case successfully.',
  NeedsReview: 'EVA flagged this case for review.',
  Failed: 'EVA was unable to complete this case.',
  CodingReview: 'EVA requires a coding review for this case.',
};

/** Friendly wording for a run step key; falls back to the raw display name, then the raw key. */
export function friendlyStepLabel(stepKey: string, rawDisplayName?: string): string {
  return STEP_LABELS[stepKey] ?? rawDisplayName ?? stepKey;
}

/** Friendly wording for a raw reason code; falls back to the raw code when unmapped. */
export function friendlyReasonLabel(reasonCode: string): string {
  return REASON_LABELS[reasonCode] ?? reasonCode;
}

/** Short friendly label for a raw reason code, sized for a grid chip; falls back to the raw code. */
export function friendlyReasonChipLabel(reasonCode: string): string {
  return REASON_CHIP_LABELS[reasonCode] ?? reasonCode;
}

/** Friendly wording for an EVA run outcome name; falls back to the raw outcome when unmapped. */
export function friendlyOutcomeLabel(outcome: string): string {
  return OUTCOME_LABELS[outcome] ?? outcome;
}

/** Human-readable labels for change-finding field tokens (spec 047 change-summary contract §2). */
const CHANGE_FIELD_LABELS: Record<string, string> = {
  EligibilityStatus: 'Eligibility status',
  TermedCoverage: 'Termed coverage',
  DeductibleRemaining: 'Deductible remaining',
  OopRemaining: 'Out-of-pocket remaining',
  DeductibleMax: 'Deductible maximum',
  OopMax: 'Out-of-pocket maximum',
  Copay: 'Copay',
  Coinsurance: 'Coinsurance',
};

/** Friendly wording for a change-finding field token; falls back to the raw token when unmapped. */
export function friendlyChangeFieldLabel(field: string): string {
  return CHANGE_FIELD_LABELS[field] ?? field;
}
